import { useRef } from "react";
import { FileImage, MessageSquare, Music2, Video as VideoIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Video } from "@/lib/api-types";
import { VideoCardMenu } from "./video-card-menu";
import { VideoStatusControl } from "./video-status-control";

type Props = {
  projectId: string;
  video: Video;
  view: "grid" | "list";
  selected: boolean;
  /** Checkbox click — accumulates to the existing multi-selection. */
  onToggleSelected: () => void;
  /** Card body single-click — replaces the entire selection with just this
   *  item (2026-07-18 spec). Optional so old callers keep compiling; when
   *  omitted the card body click falls back to onToggleSelected behavior. */
  onSelectExclusive?: () => void;
  /** "/app/watch" or "/admin/watch" — double-clicking the card navigates to `${watchPath}/${videoId}`. */
  watchPath: string;
  /**
   * "version" renders this card inside the versions page: it always opens the
   * player on double-click (never re-routes to the versions page), and its
   * menu hides "Upload new version" + "Rename". Default "asset".
   */
  cardContext?: "asset" | "version";
  /** Versions page only — shows a V{n} chip (+ "Latest") on the thumbnail. */
  versionBadge?: { number: number; latest: boolean };
};

function formatDate(value: string | undefined | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * HH:MM:SS when >= 1 hour, otherwise MM:SS. Matches Frame.io chip formatting
 * on the asset card. Returns null for images or missing/invalid duration —
 * callers just skip the chip when null.
 *
 * Exported for reuse on the guest share cards (share-landing / share-folder).
 */
export function formatDurationChip(seconds: number | null | undefined, assetType: string | undefined): string | null {
  if (assetType === "image") return null;
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function AssetIcon({ assetType, className }: { assetType?: string; className?: string }) {
  if (assetType === "audio") return <Music2 className={className} strokeWidth={1.5} />;
  if (assetType === "image") return <FileImage className={className} strokeWidth={1.5} />;
  return <VideoIcon className={className} strokeWidth={1.5} />;
}

function SelectCheckbox({
  checked,
  onToggle,
  className,
}: {
  checked: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <input
      type="checkbox"
      aria-label="Select asset"
      checked={checked}
      onClick={(e) => e.stopPropagation()}
      onChange={onToggle}
      className={className}
    />
  );
}

/**
 * Interactions (2026-07-18 spec):
 *   - Single-click on card body → replace-select this item (deselects others).
 *     Deferred ~220ms so a follow-up second click promotes to double-click
 *     without firing the select first.
 *   - Double-click on card body → open watch page. Cancels the pending
 *     single-click timer.
 *   - Checkbox → additive toggle (multi-select). stopPropagation so it doesn't
 *     bubble into the card body handler.
 *   - Menu / status control also stopPropagation on their own events.
 *   - Clicks on the parent container's empty space clear the selection —
 *     handled in project-detail/folder-detail, not here.
 */
export function VideoCard({
  projectId,
  video,
  view,
  selected,
  onToggleSelected,
  onSelectExclusive,
  watchPath,
  cardContext = "asset",
  versionBadge,
}: Props) {
  const navigate = useNavigate();
  // Inside the versions page each card is a single version — never treat it as
  // a stack (that would loop back to the versions page on open).
  const hasStack = cardContext !== "version" && (video.versionCount ?? 1) > 1;
  const durationText = formatDurationChip(video.durationSeconds, video.assetType);
  const commentCount = typeof video.commentCount === "number" ? video.commentCount : 0;
  const hasComments = commentCount > 0;

  // Multi-version stacks open the versions page (per-version browse / compare /
  // delete); single-version assets go straight to the player. versionsPath is
  // derived from watchPath so callers don't have to thread a second prop.
  const versionsPath = watchPath.replace(/\/watch$/, "/versions");
  const openWatch = () =>
    navigate(hasStack ? `${versionsPath}/${video.id}` : `${watchPath}/${video.id}`);

  // Defer single-click 220ms so a fast double-click promotes to "open" without
  // firing the select action first. Timer cancels on double-click or unmount.
  const clickTimerRef = useRef<number | null>(null);
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clickTimerRef.current !== null) return;
    clickTimerRef.current = window.setTimeout(() => {
      clickTimerRef.current = null;
      (onSelectExclusive ?? onToggleSelected)();
    }, 220);
  };
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    openWatch();
  };

  if (view === "list") {
    return (
      <div
        data-selectable-card
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={
          "flex cursor-pointer items-center gap-3 rounded-lg border bg-card px-3 py-2.5 transition-colors " +
          (selected ? "border-primary/60 ring-1 ring-primary/40" : "border-border")
        }
      >
        <SelectCheckbox checked={selected} onToggle={onToggleSelected} className="shrink-0" />
        <div className="grid h-9 w-14 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
          {video.assetType === "video" && video.cdnUrl ? (
            <video src={video.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
          ) : video.assetType === "image" && video.cdnUrl ? (
            <img src={video.cdnUrl} alt={video.title} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <AssetIcon assetType={video.assetType} className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foreground">{video.title}</div>
          <div className="flex items-center gap-2 truncate text-xs text-muted-foreground">
            <span className="truncate">
              {video.uploaderName || "Unknown"} · {formatDate(video.createdAt)}
            </span>
            {hasComments && (
              <span
                className="inline-flex shrink-0 items-center gap-1 text-muted-foreground"
                title={`${commentCount} comment${commentCount === 1 ? "" : "s"}`}
              >
                <MessageSquare className="h-3 w-3" strokeWidth={2} />
                <span className="tabular-nums">{commentCount}</span>
              </span>
            )}
            {durationText && (
              <span className="shrink-0 tabular-nums">{durationText}</span>
            )}
          </div>
        </div>
        {hasStack && (
          <span className="shrink-0 text-xs text-muted-foreground">
            V{video.latestVersionNumber} of {video.versionCount}
          </span>
        )}
        {/* Status dropdown hidden on phone — title was getting truncated to a
            single character. Change status from the ⋮ menu on mobile, or use
            the grid view (status renders below the title, no squeeze). */}
        <div className="hidden w-40 shrink-0 md:block" onClick={(e) => e.stopPropagation()}>
          <VideoStatusControl projectId={projectId} video={video} />
        </div>
        <VideoCardMenu projectId={projectId} video={video} className="shrink-0" context={cardContext} />
      </div>
    );
  }

  return (
    <div
      data-selectable-card
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      className={
        "flex cursor-pointer flex-col gap-2 rounded-xl border bg-card p-3 transition-colors " +
        (selected ? "border-primary/60 ring-1 ring-primary/40" : "border-border")
      }
    >
      <div className="relative grid aspect-video place-items-center overflow-hidden rounded-lg bg-secondary/60">
        <SelectCheckbox
          checked={selected}
          onToggle={onToggleSelected}
          className="absolute left-2 top-2 z-10"
        />
        {video.assetType === "video" && video.cdnUrl ? (
          <video src={video.cdnUrl} muted preload="metadata" className="h-full w-full object-cover" />
        ) : video.assetType === "image" && video.cdnUrl ? (
          <img src={video.cdnUrl} alt={video.title} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <AssetIcon assetType={video.assetType} className="h-7 w-7 text-muted-foreground" />
        )}
        {hasStack && (
          <span className="absolute right-1.5 top-1.5 rounded-md bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground">
            V{video.latestVersionNumber} of {video.versionCount}
          </span>
        )}
        {versionBadge && (
          <span className="absolute right-1.5 top-1.5 flex items-center gap-1">
            <span className="rounded-md bg-background/90 px-1.5 py-0.5 text-[10px] font-semibold text-foreground">
              V{versionBadge.number}
            </span>
            {versionBadge.latest && (
              <span className="rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                Latest
              </span>
            )}
          </span>
        )}
        {hasComments && (
          <span
            className="pointer-events-none absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white"
            title={`${commentCount} comment${commentCount === 1 ? "" : "s"}`}
          >
            <MessageSquare className="h-3 w-3" strokeWidth={2} />
            <span className="tabular-nums">{commentCount}</span>
          </span>
        )}
        {durationText && (
          <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white">
            {durationText}
          </span>
        )}
      </div>

      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{video.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {video.uploaderName || "Unknown"} · {formatDate(video.createdAt)}
          </div>
        </div>
        <VideoCardMenu projectId={projectId} video={video} className="shrink-0" context={cardContext} />
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <VideoStatusControl projectId={projectId} video={video} />
      </div>
    </div>
  );
}
